import { Alert, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { space } from '@/design/tokens.js';
import { useFaceEnrolment } from './face.api.js';
import { FACE_SECTION_PATH } from './face-section.js';

/**
 * A standing reminder to set up face check-in.
 *
 * **Mounted in the shell, not on a page.** Face check-in is required in
 * order to check in, so the reminder has to find somebody wherever they
 * are — a banner wired into one screen only asks the people who happen to
 * visit it. Same reasoning as the policy prompt it sits beside.
 *
 * **A banner, not a modal.** The policy prompt blocks the app because a
 * mandatory policy is a legal obligation that has to be read before
 * anything else happens. This is not that: somebody who has not scanned yet
 * can still read an announcement, book leave and do their job — they simply
 * cannot check in. Blocking the whole app over it would punish them for a
 * camera that was busy this morning.
 *
 * **Not dismissible.** It is required, and a reminder somebody can wave
 * away is a reminder for the people who were going to do it anyway. It
 * disappears the moment the scan is done, which is the only way to be rid
 * of it — and the only way anybody should want to be.
 */
export function FaceSetupPrompt() {
  const status = useFaceEnrolment();
  const navigate = useNavigate();

  // Nothing while it loads, nothing where the feature is switched off, and
  // nothing once it is done. A banner that flickers in on every page load
  // before the answer arrives is worse than one that appears a beat late.
  if (!status.data?.available || status.data.enrolled) return null;

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: space[4] }}
      message="Set up face check-in"
      description="Checking in needs your face as well as your location. It takes about twenty seconds, and until it is done you will not be able to check in."
      action={
        <Button size="small" type="primary" onClick={() => navigate(FACE_SECTION_PATH)}>
          Set it up
        </Button>
      }
    />
  );
}
